import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

import { api } from "@/lib/api";
import { getVideoEmbed } from "@/lib/videoEmbed";
import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const SITE = "https://www.monseraphinstudio.fr";

function VideoPlayer({ url, title }) {
    const embed = getVideoEmbed(url);
    if (embed.kind === "video") {
        return (
            <video
                controls
                playsInline
                preload="metadata"
                className="w-full max-h-[70vh] rounded-2xl border border-white/10 bg-black"
                data-testid="blog-video-player"
            >
                <source src={embed.embedUrl} />
            </video>
        );
    }
    // Formats courts verticaux (Shorts / TikTok / Reels)
    return (
        <div className="mx-auto w-full max-w-sm aspect-[9/16] rounded-2xl overflow-hidden border border-white/10 bg-black">
            <iframe
                src={embed.embedUrl}
                title={title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="blog-video-player"
            />
        </div>
    );
}

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setPost(null);
        setNotFound(false);
        api.get(`/blog/${slug}`)
            .then((r) => setPost(r.data))
            .catch(() => setNotFound(true));
    }, [slug]);

    const excerpt = post
        ? post.comment.length > 155
            ? post.comment.slice(0, 152).trimEnd() + "…"
            : post.comment
        : "";

    const jsonLd = post
        ? [
              {
                  "@context": "https://schema.org",
                  "@type": "BlogPosting",
                  headline: post.title,
                  description: excerpt,
                  datePublished: post.created_at,
                  dateModified: post.updated_at,
                  inLanguage: "fr-FR",
                  mainEntityOfPage: `${SITE}/blog/${post.slug}`,
                  author: { "@type": "Organization", name: "Studio Mon Séraphin", url: SITE },
                  ...(post.thumbnail_url ? { image: post.thumbnail_url } : {}),
              },
              ...(post.thumbnail_url
                  ? [
                        {
                            "@context": "https://schema.org",
                            "@type": "VideoObject",
                            name: post.title,
                            description: excerpt,
                            thumbnailUrl: post.thumbnail_url,
                            uploadDate: post.created_at,
                            embedUrl: getVideoEmbed(post.video_url).embedUrl,
                            inLanguage: "fr-FR",
                        },
                    ]
                  : []),
          ]
        : null;

    return (
        <>
            {post && (
                <SEO
                    title={`${post.title} · Blog vidéo — Studio Mon Séraphin`}
                    description={excerpt}
                    path={`/blog/${post.slug}`}
                    image={post.thumbnail_url || undefined}
                    structuredData={jsonLd}
                />
            )}
            <Navbar />
            <main className="min-h-screen bg-[#050505] pt-32 pb-24 px-6 md:px-12" data-testid="blog-post-page">
                <article className="max-w-3xl mx-auto">
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-300 transition-colors font-body"
                        data-testid="blog-back-link"
                    >
                        <ArrowLeft className="w-4 h-4" /> Toutes les vidéos
                    </Link>

                    {notFound ? (
                        <div className="mt-16 text-center" data-testid="blog-not-found">
                            <h1 className="font-display text-3xl text-white">Vidéo introuvable.</h1>
                            <p className="mt-3 text-zinc-500 font-body">Cet article a peut-être été retiré.</p>
                        </div>
                    ) : !post ? (
                        <p className="mt-16 text-zinc-500 font-body" data-testid="blog-post-loading">Chargement…</p>
                    ) : (
                        <>
                            <p className="mt-8 flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500 font-body">
                                <CalendarDays className="w-3.5 h-3.5" />
                                {format(new Date(post.created_at), "d MMMM yyyy", { locale: fr })}
                            </p>
                            <h1 className="mt-3 font-display text-3xl md:text-4xl font-light tracking-tight text-white leading-tight" data-testid="blog-post-title">
                                {post.title}
                            </h1>

                            <div className="mt-10">
                                <VideoPlayer url={post.video_url} title={post.title} />
                            </div>

                            <div className="mt-10 rounded-2xl border border-amber-300/20 bg-amber-300/[0.04] p-8" data-testid="blog-post-comment">
                                <span className="ornament font-body text-[10px] uppercase tracking-[0.4em] text-gold-gradient">
                                    Le commentaire du studio
                                </span>
                                <div className="mt-4 text-zinc-300 font-body leading-relaxed whitespace-pre-line">
                                    {post.comment}
                                </div>
                            </div>

                            <div className="mt-14 rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center">
                                <p className="font-display text-xl text-white">
                                    Envie de passer derrière le micro ?
                                </p>
                                <p className="mt-2 text-sm text-zinc-400 font-body">
                                    Réservez votre session au studio en moins de 2 minutes.
                                </p>
                                <Link
                                    to="/reserver"
                                    className="mt-5 inline-flex items-center justify-center rounded-md bg-amber-300 px-6 py-3 text-sm font-medium text-zinc-950 hover:bg-amber-200 transition-colors"
                                    data-testid="blog-post-reserve-cta"
                                >
                                    Réserver une session
                                </Link>
                            </div>
                        </>
                    )}
                </article>
            </main>
            <Footer />
        </>
    );
}
